import { useNavigate } from "react-router-dom";
import { ArrowLeft, HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// MODIFIED: Injected missing legal (Refund/Downgrade) and product (Scheduled Trips) rules.
const faqData = [
  {
    category: "Getting Started",
    questions: [
      {
        question: "What is Ping After?",
        answer: "Ping After is a dual-layer safety system operated by VANT. First, it allows you to set timed trips that automatically notify predefined contacts if you miss your check-in deadline. Second, it features an optional background 'Dead Switch' that can notify a designated Trustee if you go completely offline for an extended duration."
      },
      {
        question: "Who can use Ping After?",
        answer: "Ping After is built for adults aged 18 and older from all walks of life. It is the ideal safety net for first dates, solo travelers, elderly individuals, or anyone meeting new acquaintances who wants automated check-in protection without continuous background GPS surveillance."
      },
      {
        question: "Is there a free version?",
        answer: "Yes. Our Free tier includes 1 active trip, 1 future scheduled trip, 1 trusted group (up to 3 contacts), and automated email alerts. Our Pro tier unlocks instant SMS alerts, up to 5 scheduled trips, 5 groups (7 contacts each), and the ability to attach up to 100MB of emergency audio, photo, and video files."
      },
      {
        question: "What is the Dead Switch (Heir System)?",
        answer: "It is an optional background safeguard. If you stop opening the app or remain offline for an extended duration (e.g., 48 hours), our servers automatically dispatch a secure emergency package containing your last known trip details to your pre-selected Trustee. Please note that this is an informational safety feature and carries no legal status as a formal digital will."
      }
    ]
  },
  {
    category: "Trips & Alerts",
    questions: [
      {
        question: "Why is the minimum trip deadline set to 2 hours?",
        answer: "The 2-hour minimum duration is a strict architectural safeguard. It ensures our cloud servers and your mobile device remain perfectly synchronized across time zones, preventing accidental false alarms caused by temporary network drops or minor travel delays."
      },
      {
        question: "Can I have multiple active trips at the same time?",
        answer: "No. To prevent overlapping timers, routing confusion, and redundant notifications to your contacts, you can only have one active trip running at any given moment across both Free and Pro plans."
      },
      {
        question: "When will I get reminded before an alert goes out?",
        answer: "To preserve your device battery, we use local scheduled notifications. Your device will sound an initial local warning 1 hour before your deadline, and a final critical warning 10 minutes before expiration, giving you ample time to check in or extend your trip."
      },
      {
        question: "What if I'm running late? Can I extend my deadline?",
        answer: "Yes. You can use the 'Snooze' button to extend your deadline by 1 hour. However, to prevent system abuse and protect the reliability of emergency alerts, consecutive snoozes are strictly capped. Once exhausted, you must formally end the trip and start a new one."
      },
      {
        question: "Will alerts still send if my phone dies, loses service, or breaks?",
        answer: "Yes! Once you start a trip, the countdown runs independently on VANT secure cloud servers. If your phone loses battery, enters airplane mode, or drops network coverage, our backend will still automatically dispatch the emergency package the exact moment your deadline expires."
      }
    ]
  },
  {
    category: "Privacy & Architecture",
    questions: [
      {
        question: "Does Ping After track my location continuously in the background?",
        answer: "Absolutely not. Ping After is a timer-based check-in system, not a surveillance tool. We never track, query, or log your real-time GPS location in the background. Your coordinates are captured only when you manually initiate a trip and are shared strictly if a deadline expires without confirmation."
      },
      {
        question: "What happens to my location data when I tap 'Arrived Safely'?",
        answer: "The exact moment you confirm safety or cancel a trip, your precise GPS coordinates are permanently wiped (Hard Deleted) from our active databases. We retain only brief, non-precise metadata of your last 5 trips solely to populate your personal account dashboard."
      },
      {
        question: "How secure are the emergency alerts and media attachments?",
        answer: "Highly sensitive records are protected at rest using field-level AES-256-GCM encryption. When an alert triggers, your contacts receive a heavily randomized Smart Emergency Link hosted on isolated cloud edge networks. To protect your privacy, these public links automatically self-destruct after exactly 48 hours."
      },
      {
        question: "What information is included in the Dead Switch (Heir) package?",
        answer: "If triggered, your Trustee receives a secure disclosure containing full details of your most recent trip, along with non-precise metadata of your two preceding trips (totaling 3 trips of historical context) to assist authorities or loved ones in locating you."
      }
    ]
  },
  {
    category: "Billing, Refunds & Account Management",
    questions: [
      {
        question: "How are payments and subscriptions handled?",
        answer: "All Pro tier orders are conducted and securely processed by our authorized online reseller and Merchant of Record (MoR). The MoR handles transaction compliance and billing inquiries. VANT never stores or transmits raw credit card details on its servers."
      },
      {
        question: "What is your refund policy?",
        answer: "We offer a 14-day money-back guarantee for initial Pro subscription purchases governed by a strict zero-usage rule. You are eligible for a full refund only if exactly 0 SMS alert credits have been triggered and 0 Bytes of emergency media attachments have been uploaded. Once any premium resource is consumed, the subscription is non-refundable."
      },
      {
        question: "What happens to my uploaded files if I cancel my Pro subscription?",
        answer: "Your files are not deleted immediately. Any uploaded media files are 'soft-locked' (view-only) at the end of your billing cycle. After 60 days of inactivity on the Free tier, these large attachments are permanently deleted from our cloud storage. We will send an automated email warning 7 days prior to deletion."
      },
      {
        question: "How does permanent account deletion work?",
        answer: "You can request permanent account erasure at any time via app settings. Upon request, your account enters a 7-day grace period where all active alarms and automated emails are immediately suspended. If you do not log back in during this window, your profile, trip history, and cloud files are permanently hard-deleted from our production servers."
      }
    ]
  }
];

const FAQ = () => {
  const navigate = useNavigate();

  return (
    // MODIFIED: Added `font-legal` class to enforce typography hierarchy for long-form legal reading.
    <div className="min-h-screen bg-background font-legal">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/30">
        <div className="container mx-auto px-6 py-4">
          <button 
            onClick={() => navigate("/#footer")}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-sans text-sm md:text-base cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3.5 rounded-2xl bg-primary/10 border border-primary/20 shadow-sm">
                <HelpCircle className="w-8 h-8 md:w-10 md:h-10 text-primary" />
              </div>
              <h1 className="text-4xl md:text-6xl font-display font-bold tracking-tight text-foreground">FAQ</h1>
            </div>
            {// MODIFIED: Increased font size and contrast for better hero readability on laptop monitors.
            }
            <p className="text-muted-foreground text-lg md:text-xl max-w-2xl leading-relaxed">
              Find transparent answers about how Ping After protects you and your data.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 pb-28">
        <div className="container mx-auto px-6">
          {// MODIFIED: Increased space between category sections (`space-y-16`) to separate legal domains cleanly.
          }
          <div className="max-w-4xl mx-auto space-y-16">
            
            {faqData.map((category, categoryIndex) => (
              <div key={categoryIndex} className="scroll-mt-24">
                {// MODIFIED: Enhanced category header visual weight and spacing.
                }
                <h2 className="text-2xl md:text-3xl font-display font-semibold mb-8 flex items-center gap-3.5 text-foreground tracking-tight">
                  <span className="w-2.5 h-2.5 rounded-full bg-primary shrink-0 shadow-sm shadow-primary/50" />
                  {category.category}
                </h2>
                {// MODIFIED: Increased gap between accordion cards (`space-y-4`).
                }
                <Accordion type="single" collapsible className="space-y-4">
                  {category.questions.map((faq, faqIndex) => (
                    <AccordionItem 
                      key={faqIndex} 
                      value={`${categoryIndex}-${faqIndex}`}
                      // MODIFIED: Refined card padding, border opacity, and background tints for desktop reading comfort.
                      className="border border-border/40 rounded-2xl px-6 md:px-8 bg-card/40 hover:bg-card/80 transition-all duration-200 shadow-sm"
                    >
                      {// MODIFIED: Upgraded typography to text-base/text-lg with semibold weight for question titles.
                      }
                      <AccordionTrigger className="text-left hover:no-underline py-6 md:py-7">
                        <span className="font-semibold text-base md:text-lg text-foreground/90 pr-4 leading-snug tracking-normal">
                          {faq.question}
                        </span>
                      </AccordionTrigger>
                      {// MODIFIED: Applied `text-base`, high-contrast text, and generous line-height (`leading-8`) for long-form answers.
                      }
                      <AccordionContent className="text-muted-foreground/90 pb-7 text-base md:text-[16.5px] leading-8 font-normal pr-2 md:pr-6">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}

            {/* Still have questions */}
            {/* <div className="text-center p-8 rounded-3xl bg-gradient-to-b from-secondary/30 to-background border border-border/50 mt-12">
              <h3 className="text-xl font-display font-semibold mb-3">Still have questions?</h3>
              <p className="text-muted-foreground mb-6">
                Can't find the answer you're looking for? Reach out to our support team.
              </p>
              <Link 
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium shadow-sm hover:shadow-primary/25 hover:shadow-lg"
              >
                Contact Support
              </Link> 
              <button 
                disabled
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-secondary text-foreground opacity-50 cursor-not-allowed font-medium shadow-sm"
              >
                Soon...
              </button>
            </div> */}

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border/30">
        <div className="container mx-auto px-6 text-center text-sm text-muted-foreground font-sans">
          © {new Date().getFullYear()} VANT. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default FAQ;